import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom"; // Добавьте этот импорт
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import "./NewReport.css";
import { sendReport, checkReportDayLimit } from "../../../api/reportsApi";
import { UserContext } from "../../../contexts/CurrentUserContext.js";
import Preloader from "../../Preloader/Preloader.js";
import "./DatePickerCustomStyles.css";
import Select from "react-select";
import { getCalendar } from "../../../api/api";
import SketchDesignReport from "./SketchDesignReport/SketchDesignReport";

registerLocale("ru", ru);

const SKETCH_DESIGN_DEPARTMENT_ID = 4;

function NewReport() {
  const navigate = useNavigate(); // Добавьте эту строку

  const initialFormData = useMemo(
    () => ({
      id: 1,
      selectedDate: new Date(),
      comment: "",
      selectedBlocks: [],
      department: "",
      workType: "",
      project: "",
      effort: "00:00",
      projectBlocks: [],
      availableWorkTypes: [],
      is_overtime: false,
      is_remote: false,
    }),
    [],
  );

  const { userData } = useContext(UserContext);

  const [sections, setSections] = useState([]);
  const [formError, setFormError] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  // Добавляем состояния для периода
  const [isPeriodMode, setIsPeriodMode] = useState(false);
  const [periodStartDate, setPeriodStartDate] = useState(new Date());
  const [periodEndDate, setPeriodEndDate] = useState(new Date());
  const [periodEffort, setPeriodEffort] = useState("08:00");

  // Проверяем, является ли пользователь АДМС
  const isAdminUser = userData?.access_levels?.some(
    (level) => level.name === "АДМС",
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Устанавливаем время на начало дня
  // Для всех пользователей (включая АДМС) — запас 4 дня включая сегодня
  const minReportDate = new Date(today);
  minReportDate.setDate(today.getDate() - 3);
  minReportDate.setHours(0, 0, 0, 0);

  const [calendarData, setCalendarData] = useState({});

  // Добавляем функцию для загрузки данных календаря
  const loadCalendarData = useCallback(async () => {
    try {
      const currentDate = new Date();
      // Получаем первый день предыдущего месяца
      const firstDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        1,
      );
      // Получаем последний день следующего месяца
      const lastDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 2,
        0,
      );

      const dateFrom = firstDay.toISOString().split("T")[0];
      const dateTo = lastDay.toISOString().split("T")[0];

      console.log("Загрузка календаря с", dateFrom, "по", dateTo);

      const data = await getCalendar(dateFrom, dateTo);

      const calendarMap = {};
      data.forEach((day) => {
        calendarMap[day.date] = day;
      });

      setCalendarData(calendarMap);
    } catch (error) {
      console.error("Ошибка при загрузке календаря:", error);
    }
  }, []);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  const dayProps = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);

    const dateString = adjustedDate.toLocaleDateString("en-CA");
    const dayData = calendarData[dateString];

    // Возвращаем title только если есть event_name
    return {
      title: dayData?.event_name || undefined, // используем undefined вместо пустой строки, чтобы не показывать пустой тултип
    };
  };

  // Обновляем функцию highlightDays
  const highlightDays = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);

    const dateString = adjustedDate.toLocaleDateString("en-CA");
    const dayData = calendarData[dateString];
    const isSelectable = date >= minReportDate && date <= today;

    if (!dayData) return isSelectable ? "highlight-selectable" : "";

    // Формируем классы
    const classes = [];

    if (isSelectable) classes.push("highlight-selectable");
    if (!dayData.workday_flag) classes.push("highlight-weekend");
    if (dayData.event_name) classes.push("has-event"); // Добавляем класс для дней с событиями

    return classes.join(" ");
  };

  useEffect(() => {
    if (userData && userData.departments) {
      console.log("userData.departments:", userData.departments); // Добавим для отладки

      // Получаем все группы из всех департаментов
      const allGroups = userData.departments.flatMap(
        (dept) => dept.groups || [],
      );

      const defaultDepartment = allGroups.length === 1 ? allGroups[0].name : "";

      // Убедимся, что группа существует и имеет id
      const defaultGroup = allGroups.find(
        (group) => group.name === defaultDepartment,
      );
      if (defaultDepartment && !defaultGroup?.id) {
        console.error("Группа найдена, но не имеет id:", defaultGroup);
      }

      setSections([
        {
          ...initialFormData,
          department: defaultDepartment,
        },
      ]);
      setIsLoading(false);
    }
  }, [userData, initialFormData]);

  const updateAvailableWorkTypes = useCallback(
    (section) => {
      console.log("updateAvailableWorkTypes called with section:", section);
      console.log("userData:", userData);

      if (
        !section ||
        !userData ||
        !userData.departments ||
        !userData.projects
      ) {
        console.log("Missing required data:", { section, userData });
        return [];
      }

      // Если проект не выбран, возвращаем пустой массив
      if (!section.project) {
        console.log("No project selected");
        return [];
      }

      // Если отдел не выбран, возвращаем пустой массив
      if (!section.department) {
        console.log("No department selected");
        return [];
      }

      // Получаем массив проектов напрямую
      const projectsArray = userData.projects;
      if (!Array.isArray(projectsArray)) {
        console.log("Invalid projects structure:", userData.projects);
        return [];
      }

      const selectedProject = projectsArray.find(
        (project) => project?.id?.toString() === section.project,
      );

      console.log("Selected project:", selectedProject);

      if (!selectedProject) {
        console.log("Project not found");
        return [];
      }

      // Логируем выбранный проект
      console.log("Выбранный проект:", selectedProject);

      // Получаем все группы из всех департаментов
      const allGroups = userData.departments.flatMap(
        (dept) => dept.groups || [],
      );
      console.log("Все группы:", allGroups);

      // Собираем все доступные виды работ из всех ролей всех групп пользователя
      const allRoles = allGroups.flatMap((group) => group.roles || []);
      console.log("Все роли:", allRoles);

      const allPortalJobTypes = allRoles.flatMap(
        (role) => role.portal_job_type || [],
      );
      console.log("Все portal_job_type:", allPortalJobTypes);

      const allWorkTypes = allPortalJobTypes.filter(
        (workType) => workType && workType.id && workType.name,
      );

      // Логируем все доступные виды работ
      console.log("Все доступные виды работ:", allWorkTypes);

      // Фильтруем только по отделу
      console.log("Фильтруем по отделу:", section.department);
      const workTypes = allWorkTypes.filter((workType) => {
        // Проверяем, соответствует ли вид работ выбранному отделу
        const isDepartmentMatch = allGroups.some((group) => {
          const groupMatch = group.name === section.department;
          const hasWorkType =
            group.roles &&
            group.roles.some(
              (role) =>
                role.portal_job_type &&
                role.portal_job_type.some((type) => type.id === workType.id),
            );
          console.log(
            `Группа ${group.name}: groupMatch=${groupMatch}, hasWorkType=${hasWorkType}`,
          );
          return groupMatch && hasWorkType;
        });

        return isDepartmentMatch;
      });

      // Логируем отфильтрованные виды работ
      console.log("Отфильтрованные виды работ:", workTypes);

      // Дополнительная фильтрация для проекта "000-Без проекта" — временно отключено
      let filteredWorkTypes = workTypes;
      // if (
      //   selectedProject.name === "000-Без проекта" ||
      //   selectedProject.name?.toLowerCase().includes("без проекта") ||
      //   selectedProject.cipher_pilot === "000"
      // ) {
      //   console.log("Выбран проект '000-Без проекта', фильтруем виды работ");
      //   filteredWorkTypes = workTypes.filter((workType) => {
      //     const workTypeName = workType.name.toLowerCase();
      //     // Нормализуем название для более точного поиска
      //     const normalizedName = workTypeName.replace(/\s+/g, " ").trim();
      //
      //     const isTrainingOrMeeting =
      //       normalizedName.includes("обучение") ||
      //       normalizedName.includes("совещания") ||
      //       normalizedName.includes("совещание") ||
      //       normalizedName.includes("субботник");
      //
      //     console.log(
      //       `Вид работ "${
      //         workType.name
      //       }" (нормализованное: "${normalizedName}"): ${
      //         isTrainingOrMeeting ? "разрешен" : "запрещен"
      //       }`
      //     );
      //     return isTrainingOrMeeting;
      //   });
      //   console.log(
      //     "Виды работ после фильтрации для '000-Без проекта':",
      //     filteredWorkTypes
      //   );
      // }

      // Удаляем дубликаты
      const uniqueWorkTypes = filteredWorkTypes.filter(
        (workType, index, self) =>
          index ===
          self.findIndex(
            (t) => t.id === workType.id && t.name === workType.name,
          ),
      );

      // Логируем уникальные виды работ
      console.log("Уникальные виды работ:", uniqueWorkTypes);

      return uniqueWorkTypes;
    },
    [userData],
  );

  const handleFieldChange = useCallback(
    (sectionId, field, value) => {
      setSections((prevSections) =>
        prevSections.map((section) => {
          if (section.id === sectionId) {
            const updatedSection = { ...section, [field]: value };

            if (field === "department" || field === "project") {
              updatedSection.workType = ""; // Сбрасываем выбранный вид работ
              updatedSection.availableWorkTypes =
                updateAvailableWorkTypes(updatedSection);
            }

            return updatedSection;
          }
          return section;
        }),
      );
    },
    [updateAvailableWorkTypes],
  );

  const handleProjectChange = useCallback(
    (sectionId, projectId) => {
      setSections((prevSections) =>
        prevSections.map((section) => {
          if (section.id === sectionId) {
            // Находим проект в массиве projects напрямую
            const selectedProject = userData.projects.find(
              (p) => p.id === parseInt(projectId),
            );

            console.log("Selected project:", selectedProject); // Добавляем для отладки
            console.log("Project blocks:", selectedProject?.blocks); // Добавляем для отладки

            const updatedSection = {
              ...section,
              project: projectId,
              selectedBlocks: [],
              projectBlocks: selectedProject?.blocks || [],
            };

            updatedSection.availableWorkTypes =
              updateAvailableWorkTypes(updatedSection);
            return updatedSection;
          }
          return section;
        }),
      );
    },
    [userData.projects, updateAvailableWorkTypes],
  );

  const addSection = () => {
    const newId = sections.length + 1;
    // Получаем все группы из всех департаментов
    const allGroups = userData.departments.flatMap((dept) => dept.groups || []);
    const newSection = {
      ...initialFormData,
      id: newId,
      department: allGroups.length === 1 ? allGroups[0].name : "",
    };
    setSections([...sections, newSection]);
  };

  const removeSection = (id) => {
    if (sections.length > 1) {
      setSections(sections.filter((section) => section.id !== id));
    }
  };

  const handleBlockSelection = (sectionId, block) => {
    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id === sectionId) {
          const newSelectedBlocks =
            block === "Все"
              ? section.selectedBlocks.length === section.projectBlocks.length
                ? []
                : section.projectBlocks.map((b) => b.id)
              : section.selectedBlocks.includes(block)
                ? section.selectedBlocks.filter((b) => b !== block)
                : [...section.selectedBlocks, block];
          return { ...section, selectedBlocks: newSelectedBlocks };
        }
        return section;
      }),
    );
  };

  const isFormValid = () => {
    if (isPeriodMode) {
      // В режиме периода проверяем только основные поля без даты и трудозатрат
      return sections.every(
        (section) =>
          section.project &&
          section.workType &&
          (section.selectedBlocks.length > 0 ||
            section.projectBlocks.length === 0) &&
          periodStartDate &&
          periodEndDate &&
          periodStartDate <= periodEndDate,
      );
    } else {
      // Обычный режим - проверяем все поля
      return sections.every(
        (section) =>
          section.selectedDate &&
          section.project &&
          section.workType &&
          section.effort !== "00:00" &&
          (section.selectedBlocks.length > 0 ||
            section.projectBlocks.length === 0),
      );
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Добавьте новое состояние для отслеживания статуса каждой формы
  const [formStatuses, setFormStatuses] = useState({});

  // Информация по лимиту 8 часов за день (только для обычных и удалённых отчётов)
  const [dayLimitByDate, setDayLimitByDate] = useState({});
  const dayLimitCheckRef = useRef(null);

  const formatSecondsToHoursMinutes = (seconds) => {
    if (seconds == null || seconds < 0) return "";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h === 0) return `${m} мин`;
    if (m === 0) return `${h} ч`;
    return `${h} ч ${m} мин`;
  };

  const formatDateForDisplay = (dateStr) => {
    const [y, mo, d] = dateStr.split("-");
    return `${d}.${mo}.${y}`;
  };

  useEffect(() => {
    if (isPeriodMode || !userData?.id || !sections.length || isSubmitting)
      return;
    const nonOvertimeSections = sections.filter(
      (s) => !s.is_overtime && s.selectedDate,
    );
    if (!nonOvertimeSections.length) {
      setDayLimitByDate({});
      return;
    }
    // Группируем по дате (YYYY-MM-DD) и считаем секунды по каждой дате
    const byDate = {};
    nonOvertimeSections.forEach((s) => {
      const d = new Date(s.selectedDate);
      d.setHours(12, 0, 0, 0);
      const dateStr = d.toLocaleDateString("en-CA");
      if (!byDate[dateStr]) byDate[dateStr] = 0;
      const [h, m] = s.effort.split(":").map(Number);
      byDate[dateStr] += h * 3600 + m * 60;
    });
    if (dayLimitCheckRef.current) clearTimeout(dayLimitCheckRef.current);
    dayLimitCheckRef.current = setTimeout(async () => {
      try {
        const byDateInfo = {};
        for (const reportDateStr of Object.keys(byDate)) {
          const timeSpentSeconds = byDate[reportDateStr];
          const { limitExceeded, time_spent } = await checkReportDayLimit(
            userData.id,
            timeSpentSeconds,
            false,
            reportDateStr,
          );
          byDateInfo[reportDateStr] = {
            timeSpentSeconds: time_spent ?? 0,
            limitExceeded,
          };
        }
        setDayLimitByDate(byDateInfo);
      } catch {
        setDayLimitByDate({});
      } finally {
        dayLimitCheckRef.current = null;
      }
    }, 400);
    return () => {
      if (dayLimitCheckRef.current) clearTimeout(dayLimitCheckRef.current);
    };
  }, [sections, isPeriodMode, userData?.id, isSubmitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);
    setFormError("");
    setFormStatuses({});

    try {
      const reportsToSend = sections.map((section) => {
        const selectedDate = new Date(section.selectedDate);
        selectedDate.setHours(12, 0, 0, 0);

        const [hours, minutes] = section.effort.split(":").map(Number);
        const timeSpentInSeconds = hours * 3600 + minutes * 60;
        const dateString = selectedDate.toLocaleDateString("en-CA");

        // Получаем все группы из всех департаментов
        const allGroups = userData.departments.flatMap(
          (dept) => dept.groups || [],
        );
        const selectedGroup = allGroups.find(
          (group) => group.name === section.department,
        );

        if (!selectedGroup) {
          throw new Error(
            `Группа не найдена для отдела: ${section.department}`,
          );
        }

        if (!selectedGroup.id) {
          throw new Error(
            `ID группы не найден для отдела: ${section.department}`,
          );
        }

        return {
          user_id: userData.id,
          plan_date_start: dateString,
          plan_date_end: dateString,
          fact_date_start: dateString,
          fact_date_end: dateString,
          actual_date: dateString,
          project_id: parseInt(section.project, 10),
          blocks: section.selectedBlocks.map((blockId) => {
            const block = section.projectBlocks.find((b) => b.id === blockId);
            return block ? block.name : `Блок ${blockId}`;
          }),
          group_id: selectedGroup.id,
          portal_job_type_id: parseInt(section.workType, 10),
          comment: section.comment || "",
          time_spent: timeSpentInSeconds,
          is_overtime: section.is_overtime,
          is_remote: section.is_remote || false,
        };
      });

      for (const [index, report] of reportsToSend.entries()) {
        try {
          setFormStatuses((prev) => ({
            ...prev,
            [index]: { status: "sending", message: "Отправка..." },
          }));

          await sendReport(report);

          setFormStatuses((prev) => ({
            ...prev,
            [index]: { status: "success", message: "Успешно отправлено" },
          }));
        } catch (error) {
          setFormStatuses((prev) => ({
            ...prev,
            [index]: {
              status: "error",
              message: `Ошибка: ${error.message}`,
            },
          }));
          throw error;
        }
      }

      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setIsSubmitting(false);
        navigate("/report/all");
      }, 4000);
    } catch (error) {
      setFormError(`Произошла ошибка при отправке данных: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSections([initialFormData]);
    setFormError("");
    setShowPopup(false);
    // Сбрасываем поля периода
    setIsPeriodMode(false);
    setPeriodStartDate(new Date());
    setPeriodEndDate(new Date());
    setPeriodEffort("08:00");
  };

  // Разделим создание опций на две функции
  const createRegularEffortOptions = () => {
    const options = [];
    for (let hours = 0; hours <= 8; hours++) {
      for (let minutes = 0; minutes < 60; minutes += 15) {
        if (hours === 0 && minutes === 0) continue; // Пропускаем 00:00
        if (hours === 8 && minutes > 0) break; // Не превышаем 08:00
        const value = `${String(hours).padStart(2, "0")}:${String(
          minutes,
        ).padStart(2, "0")}`;
        options.push({ value, label: value });
      }
    }
    return options;
  };

  const createOvertimeEffortOptions = () => {
    const options = [];
    for (let hours = 1; hours <= 8; hours++) {
      for (let minutes = 0; minutes < (hours === 8 ? 30 : 60); minutes += 30) {
        const value = `${String(hours).padStart(2, "0")}:${String(
          minutes,
        ).padStart(2, "0")}`;
        options.push({ value, label: value });
      }
    }
    return options;
  };

  // Модифицируем обработчик изменения времени
  const handleEffortChange = (sectionId, selectedOption) => {
    handleFieldChange(sectionId, "effort", selectedOption.value);
  };

  // Модифицируем обработчик изменения слайдера
  const handleSliderChange = (sectionId, value) => {
    const section = sections.find((s) => s.id === sectionId);

    if (section.is_overtime) {
      // Для переработок округляем до 30 минут
      value = Math.round(value / 30) * 30;
      // Минимальное значение для переработки - 1 час
      if (value < 60) value = 60;
      // Максимальное значение - 8 часов
      if (value > 480) value = 480;
    } else {
      // Для обычных отчетов округляем до 15 минут
      value = Math.round(value / 15) * 15;
      if (value < 15) value = 15;
      if (value > 480) value = 480;
    }

    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    const formattedValue = `${String(hours).padStart(2, "0")}:${String(
      minutes,
    ).padStart(2, "0")}`;
    handleFieldChange(sectionId, "effort", formattedValue);
  };

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userData && userData.departments && userData.projects) {
      setIsLoading(false);
    } else {
      setError("Не удалось загрузить необходимые данные");
    }
  }, [userData]);

  // Функция для получения рабочих дней в периоде
  const getWorkingDaysInPeriod = (startDate, endDate) => {
    const workingDays = [];
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    const endDateTime = new Date(endDate);
    endDateTime.setHours(23, 59, 59, 999);

    while (currentDate <= endDateTime) {
      const dateString = currentDate.toLocaleDateString("en-CA");
      const dayData = calendarData[dateString];

      // Проверяем, что день рабочий (не выходной и не праздник)
      if (dayData?.workday_flag) {
        workingDays.push(dateString);
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return workingDays;
  };

  // Функция для отправки отчетов за период
  const handlePeriodSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);
    setFormError("");
    setFormStatuses({});

    try {
      const workingDays = getWorkingDaysInPeriod(
        periodStartDate,
        periodEndDate,
      );

      if (workingDays.length === 0) {
        throw new Error("В выбранном периоде нет рабочих дней");
      }

      const [hours, minutes] = periodEffort.split(":").map(Number);
      const timeSpentInSeconds = hours * 3600 + minutes * 60;

      // Получаем все группы из всех департаментов
      const allGroups = userData.departments.flatMap(
        (dept) => dept.groups || [],
      );
      const selectedGroup = allGroups.find(
        (group) => group.name === sections[0].department,
      );

      if (!selectedGroup) {
        throw new Error(
          `Группа не найдена для отдела: ${sections[0].department}`,
        );
      }

      if (!selectedGroup.id) {
        throw new Error(
          `ID группы не найден для отдела: ${sections[0].department}`,
        );
      }

      console.log(`Отправляем ${workingDays.length} отчетов за период`);

      let successCount = 0;
      let errorCount = 0;

      // Отправляем отчеты последовательно
      for (const [index, dateString] of workingDays.entries()) {
        try {
          setFormStatuses((prev) => ({
            ...prev,
            [index]: {
              status: "sending",
              message: `Отправка отчета за ${dateString}...`,
            },
          }));

          const reportData = {
            user_id: userData.id,
            plan_date_start: dateString,
            plan_date_end: dateString,
            fact_date_start: dateString,
            fact_date_end: dateString,
            actual_date: dateString,
            project_id: parseInt(sections[0].project, 10),
            blocks: sections[0].selectedBlocks.map((blockId) => {
              const block = sections[0].projectBlocks.find(
                (b) => b.id === blockId,
              );
              return block ? block.name : `Блок ${blockId}`;
            }),
            group_id: selectedGroup.id,
            portal_job_type_id: parseInt(sections[0].workType, 10),
            comment: sections[0].comment || "",
            time_spent: timeSpentInSeconds,
            is_overtime: sections[0].is_overtime,
            is_remote: sections[0].is_remote || false,
          };

          await sendReport(reportData);

          setFormStatuses((prev) => ({
            ...prev,
            [index]: {
              status: "success",
              message: `Успешно отправлено за ${dateString}`,
            },
          }));

          successCount++;
        } catch (error) {
          setFormStatuses((prev) => ({
            ...prev,
            [index]: {
              status: "error",
              message: `Ошибка за ${dateString}: ${error.message}`,
            },
          }));
          errorCount++;
        }
      }

      // Показываем итоговый результат
      if (errorCount === 0) {
        // Все отчеты отправлены успешно
        console.log(`Все ${successCount} отчетов успешно отправлены`);
        setShowPopup(true);
        setTimeout(() => {
          setShowPopup(false);
          setIsSubmitting(false);
          navigate("/report/all");
        }, 4000);
        setDayLimitByDate({});
      } else if (successCount === 0) {
        // Все отчеты завершились ошибкой
        console.error(`Все ${errorCount} отчетов завершились ошибкой`);
        setFormError(
          `Не удалось отправить ни одного отчета. Проверьте логи выше.`,
        );
        setIsSubmitting(false);
      } else {
        // Частичный успех
        console.log(
          `Отправлено ${successCount} из ${workingDays.length} отчетов. Ошибок: ${errorCount}`,
        );
        setFormError(
          `Отправлено ${successCount} из ${workingDays.length} отчетов. ${errorCount} отчетов завершились ошибкой.`,
        );
        setIsSubmitting(false);
      }
    } catch (error) {
      setFormError(`Произошла ошибка при отправке данных: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Preloader />;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!userData) {
    return <Preloader />;
  }

  const isSketchDesignDepartment = userData.departments?.some(
    (d) => d.id === SKETCH_DESIGN_DEPARTMENT_ID,
  );
  if (isSketchDesignDepartment) {
    return <SketchDesignReport />;
  }

  // Проверка доступа: проекты, группы в департаментах и типы работ (роли с portal_job_type)
  const hasProjects = userData.projects && userData.projects.length > 0;
  const allGroups =
    userData.departments?.flatMap((dept) => dept.groups || []) || [];
  const hasGroups = allGroups.length > 0;
  const hasWorkTypes = allGroups.some(
    (group) =>
      group.roles &&
      group.roles.some(
        (role) => role.portal_job_type && role.portal_job_type.length > 0,
      ),
  );

  if (!hasProjects || !hasGroups || !hasWorkTypes) {
    const reason = !hasProjects
      ? "нет назначенных проектов"
      : !hasGroups
        ? "нет группы в отделе"
        : "в вашей группе не назначены роли с типами работ (portal_job_type)";
    return (
      <p className="newreport__error">
        Вы не можете создавать отчёты: {reason}. <br />
        <span className="blurred">Обратитесь к администраторам проекта.</span>
      </p>
    );
  }

  console.log("User data for report:", {
    projects: userData.projects,
    departments: userData.departments,
    roles: userData.roles,
  });

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      width: "100%",
      maxWidth: "500px",
      backgroundColor: state.isDisabled ? "#f5f5f5" : "white",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
    }),
    menu: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      marginTop: "5px",
      zIndex: 1000,
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
    }),
  };

  const effortSelectStyles = {
    container: (provided) => ({
      ...provided,
      width: "120px",
      flexShrink: 0,
    }),
    control: (provided, state) => ({
      ...customStyles.control(provided, state),
      width: "120px",
      maxWidth: "120px",
      minHeight: "unset",
      height: "38px",
      display: "flex",
      alignItems: "center",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "38px",
      padding: "0 8px",
      display: "flex",
      alignItems: "center",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "38px",
      marginTop: "-4px",
    }),
    singleValue: (provided) => ({
      ...provided,
      margin: "0",
      marginTop: "-12px",
    }),
    placeholder: (provided) => ({
      ...provided,
      margin: "0",
      marginTop: "-12px",
    }),
  };

  return (
    <section
      className={`newreport ${isSubmitting || showPopup ? "submitting" : ""}`}
    >
      {/* Переключатель режима для АДМС пользователей */}
      {isAdminUser && (
        <div className="period-mode-toggle">
          <div className="toggle-container">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={isPeriodMode}
                onChange={(e) => setIsPeriodMode(e.target.checked)}
                disabled={isSubmitting}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="toggle-text">
              {isPeriodMode ? "Режим периода" : "Обычный режим"}
            </span>
          </div>
        </div>
      )}

      {/* Поля для выбора периода */}
      {isAdminUser && isPeriodMode && (
        <div className="period-selection">
          <h3 className="period-title">Заполнение отчета за период</h3>
          <div className="period-fields">
            <div className="period-field">
              <p className="newreport__point-title">Начало периода</p>
              <DatePicker
                selected={periodStartDate}
                onChange={(date) => setPeriodStartDate(date)}
                className="newreport__point point"
                dateFormat="dd.MM.yyyy"
                placeholderText="Выберите дату начала"
                locale="ru"
                calendarStartDay={1}
                maxDate={today}
                dayClassName={highlightDays}
                calendarClassName="custom-calendar"
                dayProps={dayProps}
                disabled={isSubmitting}
              />
            </div>
            <div className="period-field">
              <p className="newreport__point-title">Конец периода</p>
              <DatePicker
                selected={periodEndDate}
                onChange={(date) => setPeriodEndDate(date)}
                className="newreport__point point"
                dateFormat="dd.MM.yyyy"
                placeholderText="Выберите дату окончания"
                locale="ru"
                calendarStartDay={1}
                maxDate={today}
                minDate={periodStartDate}
                dayClassName={highlightDays}
                calendarClassName="custom-calendar"
                dayProps={dayProps}
                disabled={isSubmitting}
              />
            </div>
            <div className="period-field">
              <p className="newreport__point-title">Часов в день</p>
              <Select
                value={{ value: periodEffort, label: periodEffort }}
                onChange={(selectedOption) =>
                  setPeriodEffort(selectedOption.value)
                }
                options={createRegularEffortOptions()}
                styles={effortSelectStyles}
                placeholder="Время"
                noOptionsMessage={() => "Нет вариантов"}
                isDisabled={isSubmitting}
                isSearchable={false}
              />
            </div>
          </div>
          <div className="period-info">
            <p>
              Рабочих дней в периоде:{" "}
              {getWorkingDaysInPeriod(periodStartDate, periodEndDate).length}
            </p>
          </div>

          {/* Отображение прогресса отправки отчетов за период */}
          {isSubmitting && Object.keys(formStatuses).length > 0 && (
            <div className="period-progress">
              <h4>Прогресс отправки отчетов:</h4>
              <div className="progress-list">
                {Object.entries(formStatuses).map(([index, status]) => (
                  <div key={index} className={`progress-item ${status.status}`}>
                    <span className="progress-message">{status.message}</span>
                    {status.status === "sending" && (
                      <span className="progress-spinner">⏳</span>
                    )}
                    {status.status === "success" && (
                      <span className="progress-success">✅</span>
                    )}
                    {status.status === "error" && (
                      <span className="progress-error">❌</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {sections && sections.length > 0 ? (
        sections.map((section, index) => (
          <div
            key={section.id}
            className={`report-section ${index % 2 === 0 ? "even" : "odd"} ${
              formStatuses[index]?.status === "error" ? "has-error" : ""
            }`}
          >
            {formStatuses[index] && (
              <div className={`form-status ${formStatuses[index].status}`}>
                {formStatuses[index].message}
              </div>
            )}
            <div className="report-section-header">
              {sections.length > 1 && (
                <h3 className="report-section-title">Отчет {index + 1}</h3>
              )}
              {sections.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeSection(section.id)}
                  className="remove-section-button"
                  aria-label="Удалить секцию"
                  disabled={isSubmitting}
                >
                  &times;
                </button>
              )}
            </div>
            {!isPeriodMode &&
              section.selectedDate &&
              (() => {
                const d = new Date(section.selectedDate);
                d.setHours(12, 0, 0, 0);
                const dateStr = d.toLocaleDateString("en-CA");
                const info = dayLimitByDate[dateStr];
                return info ? (
                  <div
                    className={`day-limit-info-block day-limit-info-block--in-section ${
                      info.limitExceeded
                        ? "day-limit-warning"
                        : "day-limit-info"
                    }`}
                    role={info.limitExceeded ? "alert" : undefined}
                  >
                    За {formatDateForDisplay(dateStr)}:{" "}
                    {formatSecondsToHoursMinutes(info.timeSpentSeconds)} из 8 ч
                    {info.limitExceeded && " (норма)"}
                  </div>
                ) : null;
              })()}
            <ul className="newreport__points">
              <li className="newreport__point">
                <p className="newreport__point-title">Выберите отдел</p>
                {(() => {
                  // Получаем все группы из всех департаментов
                  const allGroups =
                    userData.departments?.flatMap(
                      (dept) => dept.groups || [],
                    ) || [];
                  return allGroups.length === 1 ? (
                    <input
                      className="newreport__point point"
                      value={allGroups[0].name}
                      disabled
                    />
                  ) : (
                    <Select
                      value={
                        section.department
                          ? {
                              value: section.department,
                              label: section.department,
                            }
                          : null
                      }
                      onChange={(selectedOption) =>
                        handleFieldChange(
                          section.id,
                          "department",
                          selectedOption.value,
                        )
                      }
                      options={allGroups.map((group) => ({
                        value: group.name,
                        label: group.name,
                      }))}
                      styles={customStyles}
                      placeholder="Выберите отдел"
                      noOptionsMessage={() => "Отделы не найдены"}
                      isDisabled={isSubmitting}
                    />
                  );
                })()}
              </li>

              {/* Скрываем поле даты в режиме периода */}
              {!isPeriodMode && (
                <li className="newreport__point">
                  <p className="newreport__point-title">Дата</p>
                  <DatePicker
                    selected={section.selectedDate}
                    onChange={(date) =>
                      handleFieldChange(section.id, "selectedDate", date)
                    }
                    className="newreport__point point"
                    dateFormat="dd.MM.yyyy"
                    placeholderText="Выберите дату"
                    locale="ru"
                    calendarStartDay={1}
                    minDate={minReportDate}
                    maxDate={today}
                    dayClassName={highlightDays}
                    calendarClassName="custom-calendar"
                    dayProps={dayProps}
                    renderDayContents={(day, date) => {
                      const dateString = date.toLocaleDateString("en-CA");
                      const dayData = calendarData[dateString];
                      return (
                        <div
                          className="day-content"
                          data-tooltip={dayData?.event_name || ""}
                        >
                          {day}
                        </div>
                      );
                    }}
                    disabled={isSubmitting}
                  />
                </li>
              )}

              <li className="newreport__point">
                <p className="newreport__point-title">Проект</p>
                <div className="project-select-container">
                  <Select
                    value={
                      section.project && userData && userData.projects
                        ? {
                            value: section.project,
                            label:
                              userData.projects?.find(
                                (p) => p.id === parseInt(section.project),
                              )?.name || "",
                          }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleProjectChange(section.id, selectedOption?.value)
                    }
                    options={
                      userData && userData.projects
                        ? userData.projects
                            .filter((project) => {
                              // Исключаем проекты со статусами "Завершен", "Приостановлен", "Отказ"
                              const excludedStatuses = [
                                "Завершен",
                                "Приостановлен",
                                "Отказ",
                              ];

                              // Если у проекта нет поля status, считаем его активным
                              if (!project.status) {
                                return true;
                              }

                              return !excludedStatuses.includes(project.status);
                            })
                            .map((project) => ({
                              value: project.id.toString(),
                              label: project.name,
                            }))
                        : []
                    }
                    styles={customStyles}
                    placeholder="Выберите проект"
                    noOptionsMessage={() => "Проекты не найдены"}
                    isDisabled={isSubmitting}
                  />
                </div>
              </li>

              <li className="newreport__point">
                <p className="newreport__point-title">Вид работ</p>
                <Select
                  value={
                    section.workType && section.availableWorkTypes
                      ? {
                          value: section.workType,
                          label:
                            section.availableWorkTypes.find(
                              (wt) => wt.id === parseInt(section.workType),
                            )?.name || "",
                        }
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleFieldChange(
                      section.id,
                      "workType",
                      selectedOption?.value,
                    )
                  }
                  options={
                    section.availableWorkTypes
                      ? section.availableWorkTypes.map((jobType) => ({
                          value: jobType.id.toString(),
                          label: jobType.name,
                        }))
                      : []
                  }
                  styles={customStyles}
                  placeholder={
                    section.project && section.department
                      ? "Выберите вид работ"
                      : "Сначала выберите проект и отдел"
                  }
                  noOptionsMessage={() => "Виды работ не найдены"}
                  isDisabled={
                    isSubmitting || !section.project || !section.department
                  }
                />
              </li>

              {section.project && section.projectBlocks.length > 0 && (
                <li className="newreport__point">
                  <p className="newreport__point-title">Блок</p>
                  <div className="block-options">
                    <button
                      className={`block-option ${
                        section.selectedBlocks.length ===
                        section.projectBlocks.length
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => handleBlockSelection(section.id, "Все")}
                      disabled={isSubmitting}
                    >
                      Выбрать все
                    </button>
                    {section.projectBlocks.map((block) => (
                      <button
                        key={block.id}
                        className={`block-option ${
                          section.selectedBlocks.includes(block.id)
                            ? "selected"
                            : ""
                        }`}
                        onClick={() =>
                          handleBlockSelection(section.id, block.id)
                        }
                        disabled={isSubmitting}
                      >
                        {block.name}
                      </button>
                    ))}
                  </div>
                </li>
              )}

              <li className="newreport__point">
                <p className="newreport__point-title">Комментарий</p>
                <textarea
                  className={`newreport__point point ${
                    section.task === "Другое" && !section.comment
                      ? "required"
                      : ""
                  }`}
                  value={section.comment}
                  onChange={(e) =>
                    handleFieldChange(section.id, "comment", e.target.value)
                  }
                  placeholder={`Введите комментарий${
                    section.task === "Другое" ? " (обязтельно)" : ""
                  }`}
                  required={section.task === "Другое"}
                  disabled={isSubmitting}
                ></textarea>
              </li>

              {/* Скрываем поле трудозатрат в режиме периода */}
              {!isPeriodMode && (
                <>
                  <li className="newreport__point effort-point">
                    <p className="newreport__point-title">Трудзатраты</p>
                    <div className="effort-input-container">
                      <input
                        type="range"
                        min={section.is_overtime ? "60" : "15"}
                        max="480"
                        step={section.is_overtime ? "30" : "15"}
                        value={section.effort
                          .split(":")
                          .reduce((acc, time) => acc * 60 + +time, 0)}
                        onChange={(e) =>
                          handleSliderChange(
                            section.id,
                            parseInt(e.target.value),
                          )
                        }
                        className="effort-slider"
                        disabled={isSubmitting}
                      />
                      <Select
                        value={
                          createRegularEffortOptions().find(
                            (option) => option.value === section.effort,
                          ) || null
                        }
                        onChange={(selectedOption) =>
                          handleEffortChange(section.id, selectedOption)
                        }
                        options={
                          section.is_overtime
                            ? createOvertimeEffortOptions()
                            : createRegularEffortOptions()
                        }
                        styles={effortSelectStyles}
                        placeholder="Время"
                        noOptionsMessage={() => "Нет вариантов"}
                        className="effort-select"
                        isDisabled={isSubmitting}
                        isSearchable={false}
                      />
                    </div>
                    <div className="effort-checkboxes-container">
                      <div className="overtime-checkbox">
                        <input
                          type="checkbox"
                          id={`overtime-${section.id}`}
                          checked={section.is_overtime}
                          onChange={(e) => {
                            // При включении переработки устанавливаем минимальное значение в 1 час
                            if (e.target.checked) {
                              handleFieldChange(section.id, "effort", "01:00");
                            } else {
                              // При выключении переработки устанавливаем минимальное значение в 15 минут
                              const currentMinutes = section.effort
                                .split(":")
                                .reduce((acc, time) => acc * 60 + +time, 0);
                              if (currentMinutes < 15) {
                                handleFieldChange(
                                  section.id,
                                  "effort",
                                  "00:15",
                                );
                              }
                            }
                            handleFieldChange(
                              section.id,
                              "is_overtime",
                              e.target.checked,
                            );
                          }}
                          disabled={isSubmitting}
                        />
                        <label htmlFor={`overtime-${section.id}`}>
                          Переработка
                        </label>
                      </div>
                      <div className="overtime-checkbox">
                        <input
                          type="checkbox"
                          id={`remote-${section.id}`}
                          checked={section.is_remote || false}
                          onChange={(e) => {
                            handleFieldChange(
                              section.id,
                              "is_remote",
                              e.target.checked,
                            );
                          }}
                          disabled={isSubmitting}
                        />
                        <label htmlFor={`remote-${section.id}`}>Удаленно</label>
                      </div>
                    </div>
                  </li>
                </>
              )}
            </ul>
          </div>
        ))
      ) : (
        <p>Нет доступных секций для отображения</p>
      )}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <div className="popup-icon"></div>
            <p className="popup-text">Форма успешно отправлена</p>
          </div>
        </div>
      )}
      {formError && <p className="form-error">{formError}</p>}
      <div className="newreport__buttons">
        <button
          className="newreport__button button-add button-active"
          onClick={addSection}
          disabled={isSubmitting}
        >
          +
        </button>
        <button
          className={`newreport__button button-send ${
            isFormValid() ? "button-send-active" : "button-send-disabled"
          }`}
          onClick={isPeriodMode ? handlePeriodSubmit : handleSubmit}
          disabled={isSubmitting || !isFormValid()}
        >
          {isPeriodMode ? "Отправить за период" : "Отправить"}
        </button>
        <button
          className="newreport__button button-reset"
          onClick={resetForm}
          disabled={isSubmitting}
        >
          Сбросить
        </button>
      </div>
    </section>
  );
}

export default NewReport;
